import { logger } from '@gik/analytics/utils/logger';
import type { SimilarProductsParams } from '@gik/api/products/similarProducts';
import { useSimilarProducts } from '@gik/api/products/similarProducts';
import { SkeletonNetworkError, SkeletonNetworkRetryMessage } from '@gik/core/components/NetworkError/NetworkError';
import * as NetworkStoreApi from '@gik/core/stores/NetworkStore.ts';
import bemBlock from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import {
  RelatedProducts,
  RelatedProductsSkeleton,
} from '@gik/shop/components/products/RelatedProducts/RelatedProducts';
import { Container } from '@gik/ui-legacy/Container';
import type { PageSectionProps } from '@gik/ui-legacy/gik/PageSection';
import { PageSection } from '@gik/ui-legacy/gik/PageSection';
import type { HeadingLevel } from '@gik/ui-legacy/Heading';
import { Heading } from '@gik/ui-legacy/Heading';
import React from 'react';

export interface IRelatedProductsSectionProps
  extends React.HTMLAttributes<HTMLDivElement>,
    Pick<PageSectionProps, 'variant'> {
  copy?: string;
  searchParams: SimilarProductsParams;
  headingLevel?: HeadingLevel;
}

export default function RelatedProductsSectionComp({
  className,
  copy = 'You might also like',
  searchParams,
  variant = 'lightblue',
  headingLevel,
}: IRelatedProductsSectionProps) {
  const bem = bemBlock('related-products-section');

  // Create unique request key based on search parameters
  const requestKey = `related-products-${
    searchParams.similarTo || searchParams.suggestedFor || 'default'
  }-${JSON.stringify(searchParams)}`;

  const { data: products, mutate } = useSimilarProducts(searchParams, {
    requestKey,
  });

  // Transform data and handle errors
  const transformedProducts = React.useMemo(() => {
    if (!products) return undefined;
    if (Array.isArray(products)) return products;

    logger.error('Invalid products data:', products);
    return [];
  }, [products]);

  // Show skeleton with retry/error handling when no data or NetworkStore indicates issues
  if (!transformedProducts || !NetworkStoreApi.isSuccess(requestKey)) {
    return (
      <PageSection variant={variant} className={bem('', '', className)}>
        <Container>
          <Heading level={headingLevel} className={bem('title')}>
            {copy}
          </Heading>
          <div className="relative">
            <RelatedProductsSkeleton skeletonItems={4} />
            {NetworkStoreApi.shouldShowRetryMessage(requestKey) && !NetworkStoreApi.isError(requestKey) && (
              <SkeletonNetworkRetryMessage />
            )}
            {NetworkStoreApi.isError(requestKey) && (
              <SkeletonNetworkError
                onReload={() => {
                  NetworkStoreApi.reload(requestKey);
                  mutate();
                }}
              />
            )}
          </div>
        </Container>
      </PageSection>
    );
  }

  // Only render the section if we have products
  if (transformedProducts.length === 0) {
    return null;
  }

  return (
    <PageSection variant={variant} className={bem('', '', className)}>
      <Container>
        <Heading level={headingLevel} className={bem('title')}>
          {copy}
        </Heading>
        <RelatedProducts searchParams={searchParams} products={transformedProducts} />
      </Container>
    </PageSection>
  );
}

export const RelatedProductsSection = withComponentErrorBoundary(RelatedProductsSectionComp);
